import { updateAccess } from "./auth";
import { wsLogger } from "./wsLogger";

const WS_BASE_URL =
  process.env.REACT_APP_WS_BASE_URL || "ws://localhost:8000/ws";

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000];
const REQUEST_TIMEOUT_MS = 15000;

let _reqCounter = 0;
function genRequestId() {
  _reqCounter += 1;
  return `req-${Date.now()}-${_reqCounter}`;
}

export class WsEventsClient {
  constructor({ onEvent, onStatus } = {}) {
    this._onEvent = onEvent ?? (() => {});
    this._onStatus = onStatus ?? (() => {});
    this._ws = null;
    this._closed = false;
    this._attempt = 0;
    this._reconnectTimer = null;
    this._pendingRequests = new Map(); // request_id → { resolve, reject, timer }
  }

  connect() {
    this._closed = false;
    this._open();
  }

  send(payload) {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
      throw new Error("WebSocket is not connected");
    }
    this._ws.send(JSON.stringify(payload));
  }

  sendRequest(action, data = {}, timeoutMs = REQUEST_TIMEOUT_MS) {
    return new Promise((resolve, reject) => {
      const request_id = genRequestId();

      const timer = setTimeout(() => {
        this._pendingRequests.delete(request_id);
        const err = new Error(`WS request timeout: ${action}`);
        wsLogger.error(action, request_id, err);
        reject(err);
      }, timeoutMs);

      this._pendingRequests.set(request_id, { resolve, reject, timer, action });

      try {
        this.send({ action, request_id, data });
        wsLogger.send(action, request_id, data);
      } catch (err) {
        clearTimeout(timer);
        this._pendingRequests.delete(request_id);
        wsLogger.error(action, request_id, err);
        reject(err);
      }
    });
  }

  close() {
    this._closed = true;
    clearTimeout(this._reconnectTimer);
    for (const { reject, timer } of this._pendingRequests.values()) {
      clearTimeout(timer);
      reject(new Error("WebSocket closed"));
    }
    this._pendingRequests.clear();
    if (this._ws) {
      this._ws.onclose = null;
      this._ws.close();
      this._ws = null;
    }
    this._onStatus({ status: "closed" });
  }

  _open() {
    if (this._closed) return;

    this._onStatus({ status: "connecting" });

    const url = `${WS_BASE_URL}/events/`;
    const ws = new WebSocket(url);

    ws.onopen = () => {
      this._attempt = 0;
      wsLogger.status("connected", `→ ${url}`);
      this._onStatus({ status: "connected" });
    };

    ws.onmessage = async (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }

      // Always log raw incoming frame regardless of ws_log flag
      console.log("[WS RAW]", event.data);

      wsLogger.recv(msg);

      if (msg.type === "auth_error" && msg.code === "access_needs_refresh") {
        wsLogger.status("auth_error", "refreshing token…");
        try {
          await updateAccess();
          this._reconnect();
        } catch {
          wsLogger.status("auth_failed");
          this._onStatus({ status: "auth_failed" });
          this.close();
        }
        return;
      }

      if (msg.type === "response" && msg.request_id) {
        const pending = this._pendingRequests.get(msg.request_id);
        if (pending) {
          clearTimeout(pending.timer);
          this._pendingRequests.delete(msg.request_id);
          if (msg.status >= 200 && msg.status < 300) {
            wsLogger.response(pending.action, msg.request_id, msg);
            pending.resolve(msg);
          } else {
            wsLogger.error(pending.action, msg.request_id, msg);
            pending.reject(msg);
          }
          return;
        }
      }

      this._onEvent(msg);
    };

    ws.onerror = (e) => {
      wsLogger.status("socket_error", e?.message ?? "");
      // onclose will fire after this
    };

    ws.onclose = () => {
      this._ws = null;
      if (!this._closed) {
        wsLogger.status("disconnected");
        this._onStatus({ status: "disconnected" });
        this._scheduleReconnect();
      }
    };

    this._ws = ws;
  }

  _reconnect() {
    if (this._closed) return;
    if (this._ws) {
      this._ws.onclose = null;
      this._ws.close();
      this._ws = null;
    }
    this._open();
  }

  _scheduleReconnect() {
    if (this._closed) return;
    const delay =
      RECONNECT_DELAYS[Math.min(this._attempt, RECONNECT_DELAYS.length - 1)];
    this._attempt += 1;
    wsLogger.status("reconnecting", `attempt #${this._attempt} in ${delay}ms`);
    this._onStatus({ status: "reconnecting", delay });
    this._reconnectTimer = setTimeout(() => this._open(), delay);
  }
}
